import { Injectable, OnDestroy } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { ApolloQueryResult } from '@apollo/client/core';
import { Observable, Subject, interval, merge } from 'rxjs';
import { NotificationsResult } from '../results/notification/notifications-result';
import { NotificationResult } from '../results/notification/notification-result';
import { switchMap, takeUntil } from 'rxjs/operators';


@Injectable({
  providedIn: 'root'
})
export class NotificationService implements OnDestroy {
  private componentDestroyed$: Subject<void> = new Subject();
  private notificationsInternal$: Subject<NotificationsResult> = new Subject<NotificationsResult>();
  forceRefresh$: Subject<void> = new Subject<void>();
  constructor(
    private apollo: Apollo
  ) {
    this.pullNotifications();
    this.refresh();
  }

  getNotifications(): Observable<ApolloQueryResult<NotificationsResult>> {
    return this.apollo.query<NotificationsResult>({
      query: gql`
      query notifications {
        notifications {
          totalCount
          edges {
            node {
              name
              path
              created
              isRead
              description
              icon
              severity
            }
          }
      }
     }
     `,
      variables: {}
    });
  }

  getNotification(identifier: string): Observable<ApolloQueryResult<NotificationResult>> {
    return this.apollo.query<NotificationResult>({
      query: gql`
      query notification($notificationIdentifier: String!) {
        notification(notificationIdentifier: $notificationIdentifier) {
          identifier
          name
          description
          signatures {
            name
            path
            created
            isRead
            description
            icon
            severity
          }
        }
      }
     `,
      variables: {
        'notificationIdentifier': identifier,
      }
    });
  }

  refresh(): void {
    this.forceRefresh$.next();
  }

  private pullNotifications(): void {
    const autoRefresh = interval(60000).pipe(
      takeUntil(this.componentDestroyed$)
    );

    merge(
      this.forceRefresh$,
      autoRefresh
    ).pipe(
      takeUntil(this.componentDestroyed$),
      switchMap(() => this.getNotifications())
    ).subscribe((notificationsResult: ApolloQueryResult<NotificationsResult>) => {
      this.notificationsInternal$.next(notificationsResult.data);
    });
  }

  get notifications$(): Observable<NotificationsResult> {
    return this.notificationsInternal$.asObservable();
  }

  ngOnDestroy(): void {
    this.componentDestroyed$.next();
    this.componentDestroyed$.complete();
  }
}
